import { Box, TextField, toast, Typography } from '@montugroup/design-system';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useEffect, useRef, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';

import settings from '@/constants/constants';
import AuthService from '@montugroup/circuit-shared/services/authentication.service';

import logo from '@/components/layout/alternaleaf.svg';
import { PapayaContained } from '@montugroup/circuit-shared/components/common/Papaya';

import {
  AuthCardContainer,
  AuthFormGroup,
  AuthPageContainer,
  CardHalfContent,
  CardHalfDecoration,
  MobileLogoContainer,
  MobileOnly,
  StyledLoadingButton,
} from '@/components/auth/authPage';
import { LOGIN_ROUTE } from '@/routes/route-paths';
import { zodEmailValidate } from '@montugroup/circuit-shared';
import { styled } from '@mui/material';

interface ForgotPasswordFormData {
  email: string;
}

const LoginCardBackground = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '100%',
  backgroundColor: theme.palette.primary.main,
  // eslint-disable-next-line quotes
  backgroundImage: "url('/assets/images/patientBack_AL.png')",
  backgroundPosition: 'top left',
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-start',
  padding: '2rem',
  borderTopLeftRadius: '25px',
  borderBottomLeftRadius: '25px',
}));

const BackLinkContainer = styled(Box)(({ theme }) => ({
  marginBottom: '20px',
  [theme.breakpoints.up('md')]: {
    marginBottom: '60px',
  },
}));

export function ForgotPassword() {
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<boolean>(false);

  const errorToastRef = useRef<string | number>('');

  const { register, handleSubmit, formState, control } = useForm<ForgotPasswordFormData>({
    defaultValues: {
      email: '',
    },
  });

  const { errors } = formState;
  const email = useWatch({ name: 'email', control });

  const dismissErrorToast = () => {
    if (errorToastRef.current) {
      toast.dismiss(errorToastRef.current);
      errorToastRef.current = '';
    }
  };

  const onSubmit: SubmitHandler<ForgotPasswordFormData> = async (formData) => {
    setLoading(true);
    dismissErrorToast();
    const response = await AuthService.resetPassword(formData.email);
    const isError = response instanceof Error;
    setMessage(!isError);
    if (isError) {
      errorToastRef.current = toast.error('Reset password attempt failed. Please, try again.', { autoClose: false });
    }
    setLoading(false);
  };
  useEffect(() => dismissErrorToast, []);

  const shouldShowForm = !message || email === '';

  return (
    <AuthPageContainer>
      <AuthCardContainer>
        <CardHalfDecoration>
          <LoginCardBackground>
            <PapayaContained />
          </LoginCardBackground>
        </CardHalfDecoration>
        <CardHalfContent>
          <MobileLogoContainer>
            <a href={settings.marketing.site.url} target="_blank" rel="noopener noreferrer">
              <img src={logo} alt="Alternaleaf" />
            </a>
          </MobileLogoContainer>
          <Box sx={(theme) => ({ padding: theme.spacing(6), height: '100%' })}>
            <BackLinkContainer>
              <Link to={LOGIN_ROUTE}>
                <Typography
                  sx={(theme) => ({ display: 'flex', alignItems: 'center', gap: theme.spacing(1), fontWeight: 'bold' })}
                >
                  <KeyboardArrowLeftIcon fontSize="medium" />
                  <span>Back to Login</span>
                </Typography>
              </Link>
            </BackLinkContainer>
            <form data-private>
              <Typography variant="h2" sx={(theme) => ({ marginBottom: theme.spacing(4) })}>
                {shouldShowForm ? 'Forgot Password' : 'Email Sent'}
              </Typography>

              {shouldShowForm ? (
                <AuthFormGroup>
                  <Typography sx={(theme) => ({ marginBottom: theme.spacing(4) })}>
                    Forgot your account’s password? Enter your email address and we’ll send you a recovery link.
                  </Typography>
                  <TextField
                    {...register('email', {
                      required: 'Please enter your email',
                      validate: zodEmailValidate,
                    })}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message ?? ''}
                    type="text"
                    id="userEmail"
                    placeholder="Enter email"
                    fullWidth
                    sx={{ marginBottom: 4 }}
                  />
                  <StyledLoadingButton
                    id="btnSubmit"
                    loading={loading}
                    variant="contained"
                    color="primary"
                    type="submit"
                    onClick={handleSubmit(onSubmit)}
                    data-testid="submit-button"
                  >
                    Submit
                  </StyledLoadingButton>
                </AuthFormGroup>
              ) : (
                <Typography>
                  If your email is already registered. We&apos;ll email you instructions to reset your password.{' '}
                </Typography>
              )}
            </form>
          </Box>
        </CardHalfContent>
      </AuthCardContainer>
      <MobileOnly>
        <PapayaContained sx={{ position: 'fixed' }} />
      </MobileOnly>
    </AuthPageContainer>
  );
}

export default ForgotPassword;
